import { useState, useEffect } from 'react';
import { X, Cookie, CheckCircle2, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../utils/api';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true
  });

  useEffect(() => {
    const loadSettings = async () => {

      const result = await api('/tracking/settings');

      // 🔥 FIX: Unwrap nested data structure
      const actualData = result.data?.data || result.data;

      if (result.success && actualData?.cookieConsent?.enabled) {

        setSettings(actualData.cookieConsent);
        
        // Check if user has already consented
        const consent = localStorage.getItem('cookie_consent');
        
        if (!consent) {

          setTimeout(() => setVisible(true), 800); // Smooth delayed entrance
        } else {

        }
      } else {

      }
    };

    loadSettings();
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    }));
    setVisible(false);
    
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', {
      detail: { necessary: true, analytics: true, marketing: true }
    }));
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    }));
    setVisible(false);
    
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', {
      detail: { necessary: true, analytics: false, marketing: false }
    }));
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      ...preferences,
      necessary: true, // Always true
      timestamp: new Date().toISOString()
    }));
    setVisible(false);
    
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', {
      detail: preferences
    }));
  };

  if (!visible || !settings) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      <div className="max-w-7xl mx-auto px-4 pb-6 md:px-8 md:pb-8 pointer-events-auto">
        {/* Clean white card without backdrop */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {!showDetails ? (
            // ===== SIMPLE VIEW =====
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4 md:gap-6">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#D74444] to-[#E87D3E] rounded-xl flex items-center justify-center shadow-lg">
                    <Cookie className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-[#2F2E5C] mb-2 flex items-center gap-2">
                    Cookie Preferences
                    <Shield className="w-4 h-4 text-[#D74444]" />
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 max-w-2xl">
                    We use cookies to enhance your browsing experience, analyze site traffic, and deliver personalized content. 
                    By clicking "Accept All", you consent to our use of cookies.
                  </p>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2F2E5C] to-[#E87D3E] hover:from-[#D74444] hover:to-[#E87D3E] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Accept All Cookies
                    </button>
                    
                    <button
                      onClick={handleRejectAll}
                      className="px-5 py-3 border-2 border-gray-300 hover:border-[#2F2E5C] text-gray-700 hover:text-[#2F2E5C] font-semibold rounded-xl hover:bg-gray-50 transition-all"
                    >
                      Reject All
                    </button>
                    
                    <button
                      onClick={() => setShowDetails(true)}
                      className="px-5 py-3 text-[#2F2E5C] hover:text-[#D74444] font-semibold hover:bg-gray-50 rounded-xl transition-all"
                    >
                      Customize
                    </button>
                    
                    <a
                      href="/privacy-policy"
                      className="text-sm text-gray-500 hover:text-[#D74444] underline ml-auto hidden md:inline"
                    >
                      Privacy Policy
                    </a>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setVisible(false)}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors group"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </button>
              </div>
            </div>
          ) : (
            // ===== DETAILED VIEW =====
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-[#2F2E5C] mb-2">
                    Customize Cookie Preferences
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose which cookies you'd like to accept. Some are essential for site functionality.
                  </p>
                </div>
                <button
                  onClick={() => setVisible(false)}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                </button>
              </div>

              {/* Cookie categories grid */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Necessary */}
                <div className="p-5 bg-gray-50 rounded-xl border-2 border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-[#2F2E5C]">
                      ✅ Necessary
                    </h4>
                    <span className="px-3 py-1 bg-gray-300 text-gray-700 text-xs font-bold rounded-full">
                      Always Active
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Essential for website functionality and security. Cannot be disabled.
                  </p>
                </div>

                {/* Analytics */}
                <div className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-[#2F2E5C]/40 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-[#2F2E5C]">
                      📊 Analytics
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2F2E5C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2F2E5C]" />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Help us understand visitor behavior and improve our services.
                  </p>
                </div>

                {/* Marketing */}
                <div className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-[#D74444]/40 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-[#2F2E5C]">
                      🎯 Marketing
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#D74444]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D74444]" />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Enable personalized ads and marketing communications.
                  </p>
                </div>

                {/* Preferences */}
                <div className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-purple-500/40 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-[#2F2E5C]">
                      ⚙️ Preferences
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.preferences}
                        onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500" />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Remember your settings and provide customized features.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSavePreferences}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2F2E5C] to-[#E87D3E] hover:from-[#D74444] hover:to-[#E87D3E] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Save My Preferences
                </button>
                
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-5 py-3 border-2 border-gray-300 hover:border-[#2F2E5C] text-gray-700 hover:text-[#2F2E5C] font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  Back to Simple View
                </button>

                <a
                  href="/privacy-policy"
                  className="text-sm text-gray-500 hover:text-[#D74444] underline ml-auto"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}